import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { formatPlanPrice } from '../../utils/planPrice';
import { PLAN_GATING_ENABLED } from '../../utils/plan';

function LandingPage() {
  const { t } = useTranslation('landing');
  const { t: tSub } = useTranslation('subscription');
  const { plan } = useSubscription();
  const hasSale = plan.saleActive && plan.monthly.salePrice != null;

  const features = [
    { icon: '💰', title: t('features.expenseTitle'), body: t('features.expenseBody') },
    { icon: '🎯', title: t('features.goalTitle'), body: t('features.goalBody') },
    { icon: '✅', title: t('features.checklistTitle'), body: t('features.checklistBody') },
    { icon: '🔔', title: t('features.stayOnTopTitle'), body: t('features.stayOnTopBody') },
  ];

  const steps = [
    { number: '01', title: t('steps.step1Title'), body: t('steps.step1Body') },
    { number: '02', title: t('steps.step2Title'), body: t('steps.step2Body') },
    { number: '03', title: t('steps.step3Title'), body: t('steps.step3Body') },
  ];

  const aiExamples = [
    { from: 'you', text: t('ai.example1You') },
    { from: 'ai', text: t('ai.example1Ai') },
    { from: 'you', text: t('ai.example2You') },
    { from: 'ai', text: t('ai.example2Ai') },
  ];

  return (
    <div className="landing-page">
      <Helmet>
        <title>Zlux - Personal Finance & Life Organization</title>
        <meta name="description" content="Zlux is a personal finance and life organization app: track expenses, plan savings goals, and manage daily checklists in one calm, unified dashboard." />
        <link rel="canonical" href="https://zlux.asia/" />
      </Helmet>
      <section className="landing-hero">
        <p className="eyebrow">{t('hero.eyebrow')}</p>
        <div className="landing-title-row">
          <img src="/logo-icon.png" alt="" className="landing-title-icon" />
          <h1 className="landing-title">Zlux</h1>
        </div>
        <p className="landing-subtitle">{t('hero.subtitle')}</p>
        <p className="landing-hero-body">{t('hero.body')}</p>
        <div className="landing-hero-actions">
          <Link to="/signup" className="primary-btn standout-btn">{t('hero.getStarted')}</Link>
          <Link to="/login" className="text-btn">{t('hero.login')}</Link>
        </div>
      </section>

      <section className="landing-ai-spotlight">
        <div className="landing-ai-copy">
          <span className="landing-ai-badge">✨ {t('ai.badge')}</span>
          <h2>{t('ai.heading')}</h2>
          <p>{t('ai.body')}</p>
          <Link to="/signup" className="primary-btn standout-btn">{t('ai.cta')}</Link>
        </div>
        <div className="landing-ai-preview" aria-hidden="true">
          {aiExamples.map((message, index) => (
            <div key={index} className={`landing-ai-bubble landing-ai-bubble-${message.from}`}>
              {message.text}
            </div>
          ))}
        </div>
      </section>

      <section className="landing-features">
        <p className="eyebrow">{t('features.eyebrow')}</p>
        <h2>{t('features.heading')}</h2>
        <div className="landing-feature-grid">
          {features.map((feature) => (
            <article key={feature.title} className="card landing-feature-card">
              <span className="landing-feature-icon" aria-hidden="true">{feature.icon}</span>
              <h3>{feature.title}</h3>
              <p>{feature.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Nothing is behind a paywall any more, so there is no price to quote and
          no choice to present. A "free forever" card would still be a pricing
          section — it just answers a question nobody on this page is asking — so
          the whole block only appears if gating is ever switched back on. */}
      {PLAN_GATING_ENABLED && (
        <section className="landing-pricing">
          <p className="eyebrow">{tSub('landing.heading')}</p>
          <h2>{tSub('landing.subheading')}</h2>
          <div className="pricing-grid">
            <article className="pricing-card">
              <h3>{tSub('landing.freeTitle')}</h3>
              <div className="pricing-card-price"><strong>{tSub('landing.freePrice')}</strong></div>
              <ul className="upgrade-feature-list">
                <li>{tSub('landing.freeFeature1')}</li>
                <li>{tSub('landing.freeFeature2')}</li>
                <li>{tSub('landing.freeFeature3')}</li>
              </ul>
              <Link to="/signup" className="text-btn">{tSub('landing.cta')}</Link>
            </article>
            <article className="pricing-card pricing-card-featured">
              <h3>{tSub('landing.proTitle')} {hasSale && <span className="plan-badge plan-pro">{plan.saleLabel || tSub('landing.saleBadge')}</span>}</h3>
              <div className="pricing-card-price">
                {hasSale && <span className="price-original">{formatPlanPrice(plan.monthly.price, plan.currency)}</span>}
                <strong>{formatPlanPrice(plan.monthly.effectivePrice, plan.currency)}</strong>
                <span>{tSub('interval.monthlyPeriod')}</span>
              </div>
              <p className="pricing-card-note">{tSub('landing.intervalNote')}</p>
              <ul className="upgrade-feature-list">
                <li>{tSub('landing.proFeature1')}</li>
                <li>{tSub('landing.proFeature2')}</li>
                <li>{tSub('landing.proFeature3')}</li>
              </ul>
              <Link to="/signup" className="primary-btn standout-btn">{tSub('landing.cta')}</Link>
            </article>
          </div>
        </section>
      )}

      <section className="landing-steps">
        <p className="eyebrow">{t('steps.eyebrow')}</p>
        <h2>{t('steps.heading')}</h2>
        <div className="landing-steps-grid">
          {steps.map((step) => (
            <article key={step.number} className="card landing-step-card">
              <span className="landing-step-number">{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
        <Link to="/how-to-use" className="footer-link-strong">{t('steps.fullWalkthrough')}</Link>
      </section>

      <section className="landing-cta">
        <h2>{t('cta.heading')}</h2>
        <div className="landing-hero-actions">
          <Link to="/signup" className="primary-btn standout-btn">{t('cta.createAccount')}</Link>
          <Link to="/login" className="text-btn">{t('cta.haveAccount')}</Link>
        </div>
      </section>

      <footer className="landing-footer">
        <p>{t('footerRights', { year: new Date().getFullYear() })}</p>
      </footer>
    </div>
  );
}

export default LandingPage;
